import React, { useState } from 'react';
import AppBar          from '@mui/material/AppBar';
import Toolbar         from '@mui/material/Toolbar';
import Typography      from '@mui/material/Typography';
import Box             from '@mui/material/Box';
import Avatar          from '@mui/material/Avatar';
import Menu            from '@mui/material/Menu';
import MenuItem        from '@mui/material/MenuItem';
import Divider         from '@mui/material/Divider';
import ListItemIcon    from '@mui/material/ListItemIcon';
import IconButton      from '@mui/material/IconButton';
import Tooltip         from '@mui/material/Tooltip';
import HomeIcon        from '@mui/icons-material/Home';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { DRAWER_WIDTH }  from './Sidebar';
import { useAuth }       from '../../context/AuthContext';

/* Avatar colour derived from username — stable across re-renders */
const AVATAR_COLOURS = [
  '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B',
  '#EF4444', '#EC4899', '#06B6D4', '#84CC16',
];
const avatarColour = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLOURS[Math.abs(h) % AVATAR_COLOURS.length];
};

const TopBar = ({ title, onMenuClick }) => {
  const { user, goHome } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const displayName = user?.username || 'User';
  const initials  = displayName.slice(0, 2).toUpperCase();
  const bgColour  = avatarColour(displayName);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
          /* Subtle glass-white — contrasts sharply with the dark sidebar */
          background: 'rgba(248,250,255,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
        }}
      >
        <Toolbar sx={{ minHeight: '60px !important', px: '24px !important' }}>

          {/* Hamburger — opens the off-canvas sidebar drawer, mobile only */}
          <IconButton
            onClick={onMenuClick}
            size="small"
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              mr: 1.5,
              color: '#64748B',
              border: '1px solid rgba(203,213,225,0.6)',
              borderRadius: '10px',
              '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59,130,246,0.06)' },
            }}
          >
            <MenuRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>

          {/* Home — back to the Portal's module launcher, same as MaterialWatch */}
          <Tooltip title="Back to Portal">
            <IconButton
              onClick={goHome}
              size="small"
              sx={{
                mr: 1.5,
                color: '#64748B',
                border: '1px solid rgba(203,213,225,0.6)',
                borderRadius: '10px',
                '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59,130,246,0.06)' },
              }}
            >
              <HomeIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>

          {/* Page title */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                fontSize: '1.05rem',
                color: '#0F172A',
                letterSpacing: '-0.2px',
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Right cluster */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

            {/* Divider */}
            <Box sx={{ width: '1px', height: 28, bgcolor: 'rgba(203,213,225,0.7)' }} />

            {/* User button */}
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.2,
                py: 0.55,
                borderRadius: '10px',
                cursor: 'pointer',
                border: '1px solid rgba(203,213,225,0.6)',
                background: 'rgba(255,255,255,0.7)',
                transition: 'all 0.18s',
                '&:hover': {
                  background: '#fff',
                  borderColor: 'rgba(148,163,184,0.7)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: bgColour,
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                {initials}
              </Avatar>
              <Typography sx={{
                fontSize: 13,
                fontWeight: 600,
                color: '#334155',
                fontFamily: "'Inter', sans-serif",
                maxWidth: 110,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayName}
              </Typography>
              <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            mt: 1,
            borderRadius: '12px',
            border: '1px solid rgba(226,232,240,0.8)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
            background: '#fff',
            fontFamily: "'Inter', sans-serif",
          },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: bgColour, fontSize: 13, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            {user?.email && (
              <Typography sx={{ fontSize: 11.5, color: '#64748B', lineHeight: 1.2 }}>
                {user.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(226,232,240,0.8)' }} />

        {/* Back Home — password management and sign-out both live in the
            Portal now, not inside this module. */}
        <MenuItem
          onClick={() => { setAnchorEl(null); goHome(); }}
          sx={{ gap: 1.2, py: 1, fontSize: 13.5, color: '#334155', fontFamily: "'Inter', sans-serif",
                '&:hover': { bgcolor: 'rgba(59,130,246,0.06)' } }}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            <HomeIcon sx={{ fontSize: 17, color: '#64748B' }} />
          </ListItemIcon>
          Back Home
        </MenuItem>
      </Menu>
    </>
  );
};

export default TopBar;
